import { useCallback, useRef, useEffect } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Text,
  FormControl,
  Switch,
  RadioGroup,
  Radio,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { NotificationDirectType } from '@/model';
import { SelectField } from '@/components/SelectField';
import { NOTIFICATION_TARGET_COMPONENTS, translateApplicationComponentId } from '@/model/application-component';
import { useCreateNotificationMutation } from '@/services/queries/notification-push';
import { CreateNotificationPayload } from '@/services/request/notification-push';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';

export interface NotificationCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const NotificationCreateModal = ({ isOpen, onClose, onDone }: NotificationCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const createItemMutation = useCreateNotificationMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    watch,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: '',
      content: '',
      direct: false,
      direct_type: undefined,
      screen: undefined,
      hyperlink: undefined,
      has_scheduled_at: false,
      scheduled_at: undefined,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formValues = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    reset();
    onClose?.();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!data) return;

      const payload: CreateNotificationPayload = {
        title: data.title,
        content: data.content,
      };

      if (data.direct && data.direct_type === NotificationDirectType.APP_AREA) {
        payload.screen = data.screen;
      }

      if (data.direct && data.direct_type === NotificationDirectType.EXTERNAL_LINK) {
        payload.hyperlink = data.hyperlink;
      }

      if (data.has_scheduled_at && data.scheduled_at) {
        // `datetime-local` retorna hora local sem timezone; new Date() interpreta
        // como hora local do navegador e toISOString() converte para UTC, que é
        // o formato esperado pelo backend (dayjs.tz na Fase 2).
        payload.scheduled_at = new Date(data.scheduled_at).toISOString();
      }

      createItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          toast({
            status: 'success',
            title: 'Notificação criada com sucesso!',
          });
          reset();
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || 'Não foi possível criar a notificação. Tente novamente.',
          });
        },
      });
    },
    [onDone, createItemMutation, toast, reset],
  );

  // Reseta o formulário quando o modal é aberto
  useEffect(() => {
    if (isOpen) {
      reset({
        title: '',
        content: '',
        direct: false,
        direct_type: undefined,
        screen: undefined,
        hyperlink: undefined,
        has_scheduled_at: false,
        scheduled_at: undefined,
      });
    }
  }, [isOpen, reset]);

  // Limpa campos condicionais com base em direct e direct_type
  useEffect(() => {
    if (!formValues.direct) {
      setValue('direct_type', undefined);
      setValue('screen', undefined);
      setValue('hyperlink', undefined);
      clearErrors(['direct_type', 'screen', 'hyperlink']);
    } else if (formValues.direct_type === NotificationDirectType.APP_AREA) {
      setValue('hyperlink', undefined);
      clearErrors('hyperlink');
    } else if (formValues.direct_type === NotificationDirectType.EXTERNAL_LINK) {
      setValue('screen', undefined);
      clearErrors('screen');
    }
  }, [formValues.direct, formValues.direct_type, setValue, clearErrors]);

  // Limpa o campo de data quando o agendamento é desativado
  useEffect(() => {
    if (!formValues.has_scheduled_at) {
      setValue('scheduled_at', undefined);
      clearErrors('scheduled_at');
    }
  }, [formValues.has_scheduled_at, setValue, clearErrors]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              Criar notificação
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label='Título*:'
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
              {...register('title')}
            />
            <Text fontSize='sm' color='gray.500' mt='-4'>
              Máx de caracteres 50
            </Text>

            <InputField
              withoutFloating
              label='Descrição*:'
              rootProps={{ isInvalid: !!errors?.content, errorMessage: errors?.content?.message }}
              {...register('content')}
            />
            <Text fontSize='sm' color='gray.500' mt='-4'>
              Máx de caracteres 100
            </Text>

            <Controller
              name='direct'
              control={control}
              render={({ field }) => (
                <FormControl display='flex' gap={2} alignItems='center' isInvalid={!!errors?.direct}>
                  <Switch
                    name={field.name}
                    ref={field.ref}
                    isChecked={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                  <Text fontWeight='medium'>Direcionar</Text>
                </FormControl>
              )}
            />

            {formValues?.direct ? (
              <Controller
                name='direct_type'
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    display={{ base: 'flex', md: 'initial' }}
                    flexDirection={{ base: 'column', md: 'initial' }}
                    gap={{ base: 6, md: 'initial' }}
                  >
                    <Flex direction='column'>
                      <Flex direction={{ base: 'column', md: 'initial' }} gap={{ base: 3, md: 'initial' }}>
                        <Radio minW='180px' value={NotificationDirectType.APP_AREA}>
                          Área do aplicativo:
                        </Radio>
                        <SelectField
                          _disabled={{ opacity: '0.5', cursor: 'not-allowed' }}
                          disabled={formValues.direct_type !== NotificationDirectType.APP_AREA}
                          width='100%'
                          {...register('screen')}
                        >
                          <option value=''>Selecione</option>
                          {NOTIFICATION_TARGET_COMPONENTS.map(id => (
                            <option key={id} value={id}>
                              {translateApplicationComponentId(id)}
                            </option>
                          ))}
                        </SelectField>
                      </Flex>
                      {errors?.screen ? <Text color='red'>{errors?.screen?.message}</Text> : null}
                    </Flex>

                    <Flex direction='column'>
                      <Flex direction={{ base: 'column', md: 'initial' }} gap={{ base: 1, md: 'initial' }}>
                        <Radio minW='180px' value={NotificationDirectType.EXTERNAL_LINK}>
                          Link externo:
                        </Radio>
                        <InputField
                          width='100%'
                          disabled={formValues.direct_type !== NotificationDirectType.EXTERNAL_LINK}
                          {...register('hyperlink')}
                        />
                      </Flex>
                      {errors?.hyperlink ? <Text color='red'>{errors?.hyperlink?.message}</Text> : null}
                    </Flex>

                    {errors?.direct_type ? <Text color='red'>{errors?.direct_type?.message}</Text> : null}
                  </RadioGroup>
                )}
              />
            ) : null}

            <Controller
              name='has_scheduled_at'
              control={control}
              render={({ field }) => (
                <FormControl display='flex' gap={2} alignItems='center' isInvalid={!!errors?.has_scheduled_at}>
                  <Switch
                    name={field.name}
                    ref={field.ref}
                    isChecked={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                  <Text fontWeight='medium'>
                    {t('pages.communication_central.components.notification_create_modal.schedule_send_label')}
                  </Text>
                </FormControl>
              )}
            />

            {formValues?.has_scheduled_at ? (
              <InputField
                withoutFloating
                label={t('pages.communication_central.components.notification_create_modal.scheduled_at_label')}
                type='datetime-local'
                fullDatePickerArea
                min={formatDateToDateTimeLocalInputValue(new Date())}
                rootProps={{ isInvalid: !!errors?.scheduled_at, errorMessage: errors?.scheduled_at?.message }}
                {...register('scheduled_at')}
              />
            ) : null}
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={handleClose}>
              Cancelar
            </Button>
            <Button onClick={handleConfirm}>Criar notificação</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>

      <CenteredSpinner isLoading={createItemMutation.isLoading} />
    </Modal>
  );
};
